import { ActivityLogIcon, EnterIcon, ExitIcon, HeightIcon, QuestionMarkCircledIcon } from '@radix-ui/react-icons'
import { Text, type TextProps } from '@radix-ui/themes'
import { MEASUREMENT_TYPES, type MeasurementType, type Stations } from '@willie/schemas'
import type { IconType } from 'react-icons'
import { LuCloudRain } from 'react-icons/lu'

// Every @radix-ui/react-icons export shares this type; the package does not re-export it.
type RadixIcon = typeof EnterIcon

interface StationTypeDescriptor {
  Glyph: RadixIcon | IconType
  tone: TextProps['color']
  label: string
}

const VOLUME_DESCRIPTORS: Record<Stations.WaterStationType, StationTypeDescriptor> = {
  entry: { Glyph: EnterIcon, tone: 'violet', label: 'Entry station' },
  usage: { Glyph: ActivityLogIcon, tone: 'teal', label: 'Usage station' },
  discharge: { Glyph: ExitIcon, tone: 'brown', label: 'Discharge station' }
}

const WATER_LEVEL_DESCRIPTOR: StationTypeDescriptor = {
  Glyph: HeightIcon,
  tone: 'blue',
  label: 'Water level station'
}

const RAINFALL_DESCRIPTOR: StationTypeDescriptor = {
  Glyph: LuCloudRain,
  tone: 'cyan',
  label: 'Rainfall station'
}

const UNKNOWN_DESCRIPTOR: StationTypeDescriptor = {
  Glyph: QuestionMarkCircledIcon,
  tone: 'gray',
  label: 'Unknown station type'
}

const isMeasurementType = (value: string): value is MeasurementType => Object.hasOwn(MEASUREMENT_TYPES, value)

const describeStation = (
  measurementType: string,
  stationType: Stations.WaterStationType | null
): StationTypeDescriptor => {
  if (!isMeasurementType(measurementType)) return UNKNOWN_DESCRIPTOR

  switch (measurementType) {
    case 'water_level':
      return WATER_LEVEL_DESCRIPTOR
    case 'rainfall':
      return RAINFALL_DESCRIPTOR
    case 'water_volume':
      // water_stations.station_type is an unconstrained text column, so the union can lie.
      return (stationType && VOLUME_DESCRIPTORS[stationType]) || UNKNOWN_DESCRIPTOR
  }
}

export interface StationTypeIconProps {
  measurementType: string
  stationType: Stations.WaterStationType | null
  size?: TextProps['size']
}

export const StationTypeIcon = ({ measurementType, stationType, size = '2' }: StationTypeIconProps) => {
  const { Glyph, tone, label } = describeStation(measurementType, stationType)

  return (
    <Text
      as="span"
      size={size}
      color={tone}
      role="img"
      aria-label={label}
      title={label}
      className="inline-flex items-center"
    >
      <Glyph width="1em" height="1em" aria-hidden />
    </Text>
  )
}
